import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { changes, fields } from 'src/components/utils/fields'
import { formatYearToDate, parseDateToYear, formatDateForInput, formatDateForAPI } from 'src/components/utils/dateFormatter';
import { useMediaQuery } from '@mui/material'
import { BufferService, LastMovementService, LoadingContext, M_Button, M_Fieldset, M_Header, M_MessageBoard, NavigateContext } from 'src/M_Components'
import { ShipmentService } from 'src/M_Components/M_Utils/shipment.service'
import { Anchor, Book, Cached, EditLocation, DirectionsBoat, Group, Refresh } from '@mui/icons-material'


export const LastMovementCrud = () => {
    const [id, setId] = useState(sessionStorage.getItem('selectedTab'))
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)
    const [data, setData] = useState({})
    const [open, setOpen] = useState(false)
    const [passedRow, setPassedRow] = useState({})
    const [operation, setOperation] = useState('')
    const [isFirstTime, setIsFirstTime] = useState(true)
    const [leavingPage, setLeavingPage] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [apiErrorOpen, setApiErrorOpen] = useState(false)
    const [apiErrorMessage, setApiErrorMessage] = useState('')

    const locationService = new BufferService('Location')
    const bookingEquipmentService = new BufferService('BookingEquipment')
    const voyageService = new BufferService('Voyage')
    const shipmentService = new ShipmentService()

    const [allowedStatuses, setAllowedStatuses] = useState([])
    const movementTypeEquipmentStatusService = new BufferService('MovementTypeEquipmentStatus', 'secondary')
    const equipmentService = new BufferService('Equipment', 'secondary')
    const equipmentTypeService = new BufferService('EquipmentType', 'secondary')
    const equipmentServiceService = new BufferService('EquipmentService', 'secondary')
    const equipmentStatusService = new BufferService('EquipmentStatus', 'secondary')
    const equipmentConditionService = new BufferService('EquipmentCondition', 'secondary')
    const equipmentLeasingService = new BufferService('EquipmentLeasing', 'secondary')
    const movementService = new BufferService('Movement', 'secondary')
    const movementTypeService = new BufferService('MovementType', 'secondary')
    const transportService = new BufferService('Transport', 'secondary')
    const transportMeansService = new BufferService('TransportMeans', 'secondary')
    const lastMovementService = new LastMovementService()

    const sessionKeys = ['operation', 'data', 'open', 'passedRow', 'leavingPage']

    const matches = useMediaQuery('(min-width:1800px)')
    const matches2 = useMediaQuery('(min-width:1700px)')
    const matches3 = useMediaQuery('(min-width:1490px)')
    const matches4 = useMediaQuery('(min-width:1450px)')
    const matches5 = useMediaQuery('(min-width:1420px)')
    const matches6 = useMediaQuery('(min-width:1380px)')
    const matches7 = useMediaQuery('(min-width:1280px)')
    const matches8 = useMediaQuery('(min-width:1230px)')
    const matches9 = useMediaQuery('(min-width:1190px)')
    const matches10 = useMediaQuery('(min-width:1150px)')
    const matches11 = useMediaQuery('(min-width:970px)')
    const matches12 = useMediaQuery('(min-width:930px)')
    const matches13 = useMediaQuery('(min-width:850px)')
    const matches14 = useMediaQuery('(min-width:870px)')
    const matches15 = useMediaQuery('(min-width:615px)')
    const matches16 = useMediaQuery('(min-width:440px)')

    const boundOptions = [
        { label: t('Export'), value: 'export' },
        { label: t('Import'), value: 'import' },
        { label: t('Transit'), value: 'transit' },
        { label: t('Internal'), value: 'internal' },
        { label: t('Operations'), value: 'operations' },
        { label: t('Other'), value: 'other' },
    ]
    const tradeBoundMap = {
        'e': 'Export', 'E': 'Export',
        'i': 'Import', 'I': 'Import',
        't': 'Transit', 'T': 'Transit',
        'n': 'Internal', 'N': 'Internal',
        'p': 'Operations', 'P': 'Operations',
        'o': 'Other', 'O': 'Other',
    }
    const transRespOptions = [
        { label: t('Merchant'), value: 'merchant' },
        { label: t('Liner'), value: 'liner' },
        { label: t('Agency'), value: 'agency' },
        { label: t('Other'), value: 'other' },
    ]
    const tempUnitOptions = [
        { label: t('Celsius'), value: 'celsius' },
        { label: t('Fahrenheit'), value: 'fahrenheit' },
        { label: t('Other'), value: 'other' },
    ]
    const fieldOptionsMap = {
        'trBound': boundOptions,
        'tempUnit': tempUnitOptions,
    }

    // ─────────────────────────────────────────────────────────────────
    // fetchBookingRelatedData
    // Single call to ViewBooking/{id} — returns full nested response.
    // Called when user selects a Booking AND on Edit/Copy load.
    // ─────────────────────────────────────────────────────────────────
    const fetchBookingRelatedData = async (bookingId) => {
        if (!bookingId) return

        const viewBookingService = new BufferService('ViewBooking')
        viewBookingService.filter(`/${bookingId}`)
            .then(res => {
                if (!res) return

                const b  = res.Booking
                const sp = res.ShipmentPlusList?.[0]
                const s  = sp?.Shipment
                const dp = sp?.ShipmentDetailPlusList?.[0]
                const sd = dp?.ShipmentDetail
                const be = dp?.BookingEquipment

                if (!b) return

                // Booking core
                changes('blNumber',             b.BLNumber              || '', setData)
                changes('bookingReference',     b.ClientReference       || '', setData)
                changes('quoteNumber',          b.QuoteNumber           || '', setData)
                changes('transhipment',         b.Transhipment === 'S'  || false, setData)
                changes('mrn',                  b.Mrn                   || '', setData)
                changes('comments',             b.Comments              || '', setData)
                changes('documentQuantity',     b.DocumentQuantity?.toString() || '', setData)
                changes('accountingA',          b.AccountingA           || '', setData)

                // Transport & voyage
                changes('voyage',               b.VoyageNumber          || '', setData)
                changes('vesselCode',           b.VesselCode            || '', setData)
                changes('vesselName',           b.VesselName            || '', setData)

                // Ports — codes
                changes('loadingPortCode',      b.LoadingPortCode           || '', setData)
                changes('dischargePortCode',    b.DischargePortCode         || '', setData)
                changes('originalPortCode',     b.OriginalPortCode          || '', setData)
                changes('finalPortCode',        b.FinalPortCode             || '', setData)

                // Ports — names
                changes('loadingPortName',      b.LocationLoadingPortName   || '', setData)
                changes('receiptPortName',      b.LocationReceiptPortName   || '', setData)
                changes('dischargePortName',    b.LocationDischargePortName || '', setData)
                changes('deliveryPortName',     b.LocationDeliveryPortName  || '', setData)

                // Ports — IDs
                changes('locationReceiptId',    b.LocationReceiptId         || '', setData)
                changes('locationDeliveryId',   b.LocationDeliveryPortId    || '', setData)

                // Client
                changes('clientId',             b.ClientId      || '', setData)
                changes('clientCode',           b.ClientCode    || '', setData)
                changes('clientVat',            b.ClientVAT     || '', setData)

                // Shipper
                changes('shipperId',            b.ShipperId     || '', setData)
                changes('shipperCode',          b.ShipperCode   || '', setData)
                changes('shipperName',          b.ShipperName   || '', setData)

                // Consignee
                changes('consigneeId',          b.ConsigneeId   || '', setData)
                changes('consigneeCode',        b.ConsigneeCode || '', setData)
                changes('consigneeName',        b.ConsigneeName || '', setData)

                // Notify
                changes('notifyId',             b.NotifyId      || '', setData)
                changes('notifyCode',           b.NotifyCode    || '', setData)
                changes('notifyName',           b.NotifyName    || '', setData)

                // Other IDs
                changes('incotermsId',          b.IncotermsId           || '', setData)
                changes('customStatusId',       b.CustomStatusId        || '', setData)
                changes('shippingConditionId',  b.ShippingConditionId   || '', setData)
                changes('currencyId',           b.CurrencyId            || '', setData)
                changes('processStatusId',      b.ProcessStatusId       || '', setData)
                changes('userCompanyId',        b.UserCompanyId         || '', setData)
                changes('externalLineId',       b.ExternalLineId        || '', setData)
                changes('invoiceId',            b.InvoiceId             || '', setData)
                changes('shippingConditionId',  b.ShippingConditionId   || '', setData)
                changes('shippingCodeId',       b.ShippingConditionId   || '', setData)

                // Shipment
                if (s) {
                    changes('grossWeight',      s.GrossWeight?.toString()   || '', setData)
                    changes('netWeight',        s.NetWeight?.toString()     || '', setData)
                    changes('nrPacks',          s.NrPacks?.toString()       || '', setData)
                    changes('temp',             s.Temp?.toString()          || '', setData)
                    changes('ucn',              s.UCN                       || '', setData)
                    changes('commodityId',      s.IdCommodity               || '', setData)
                    changes('commodityName',    s.CommodityName             || '', setData)
                    changes('commodityCd',      s.CommodityCode             || '', setData)
                }

                // ShipmentDetail
                if (sd) {
                    changes('numbers',          sd.Numbers              || '', setData)
                    changes('marks',            sd.Marks                || '', setData)
                    changes('weight',           sd.Weight?.toString()   || '', setData)
                    changes('description',      sd.Description          || '', setData)
                    changes('nrPacks',          sd.Package?.toString()  || '', setData)
                }

                // BookingEquipment
                if (be) {
                    changes('bookingEquipmentId', be.Id                             || '', setData)
                    changes('sealNumberA',        be.SealNumberA                   || '', setData)
                    changes('sealNumberB',        be.SealNumberB                   || '', setData)
                    changes('goodsDetails',       be.GoodsDetails                  || '', setData)
                    changes('transportDetails',   be.TransportDetails              || '', setData)
                    changes('loadingPortId',      be.IdTerminalLoadLocation        || b.LoadingPortId  || '', setData)
                    changes('dischargePortId',    be.IdTerminalDischargeLocation   || b.DischargePortId || '', setData)
                }

                // Voyage — fetch to get TradeBound, ETA/ETD and voyageId
                if (b.VoyageNumber) {
                    const vSvc = new BufferService('Voyage')
                    vSvc.filter(`?filter=VoyageNumber eq '${b.VoyageNumber}'&page=1&pagesize=1`)
                        .then(vRes => {
                            if (!vRes?.Items?.length) return
                            const v = vRes.Items[0]
                            const mappedBound = tradeBoundMap[v.TradeBound] || ''
                            changes('trBound',       mappedBound                                         , setData)
                            changes('trBoundValue',  mappedBound                                         , setData)
                            changes('voyageId',      v.Id                                        || ''   , setData)
                            changes('voyageDateETA', v.VoyageDateETA ? v.VoyageDateETA.split('T')[0] : '', setData)
                            changes('voyageTimeETA', v.VoyageTimeETA                             || ''   , setData)
                            changes('voyageDateETD', v.VoyageDateETD ? v.VoyageDateETD.split('T')[0] : '', setData)
                            changes('voyageTimeETD', v.VoyageTimeETD                             || ''   , setData)
                        })
                }
            })
    }

    const autoIncrementMovKey = (equipmentId, forceRun = false) => {
        if (!equipmentId || isEditMode) return
        movementService.filter(`/byequipment/${equipmentId}`)
            .then(response => {
                if (response?.name === 'AxiosError' || !response) return
                const items = Array.isArray(response) ? response : response.Items
                if (!items?.length) {
                    changes('movementKey', '1', setData)
                    return
                }
                const keys = items
                    .map(m => parseInt(m.MovementKey))
                    .filter(n => !isNaN(n))

                const maxKey = keys.length ? Math.max(...keys) : 0
                changes('movementKey', (maxKey + 1).toString(), setData)
            })
    }

    const equipmentReturnFunc = (data, field, obj) => {
        field.fields?.forEach(keyValue => {
            changes(keyValue.value, obj?.[keyValue.key]?.toString() ?? '', setData)
        })
        if (obj?.Id) autoIncrementMovKey(obj.Id)
    }

    // Fields
    const EquipmentFields = [
        {
            display: t('Prefix'),
            required: !isEditMode,
            code: 'prefix',
            type: isEditMode ? 'disabled' : 'foreignKey',
            entity: 'Equipment',
            api: 'secondary',
            filterField: 'Prefix',
            labelFields: ['Prefix'],
            fields: [{ key: 'Prefix', value: 'prefix' }, { key: 'Number', value: 'number' }, { key: 'CheckDigit', value: 'cd' }, { key: 'EquipmentTypeId', value: 'equipmentTypeId' }, { key: 'EquipmentTypeCode', value: 'equipmentTypeName' }, { key: 'Payload', value: 'payload' }, { key: 'InsideLength', value: 'insideLength' }, { key: 'Id', value: 'equipmentId' }],
            returnFunc: equipmentReturnFunc
        },
        {
            display: t('Number'),
            required: !isEditMode,
            code: 'number',
            type: isEditMode ? 'disabled' : 'foreignKey',
            entity: 'Equipment',
            api: 'secondary',
            filterField: 'Number',
            labelFields: ['Number'],
            fields: [{ key: 'Prefix', value: 'prefix' }, { key: 'Number', value: 'number' }, { key: 'CheckDigit', value: 'cd' }, { key: 'EquipmentTypeId', value: 'equipmentTypeId' }, { key: 'EquipmentTypeCode', value: 'equipmentTypeName' }, { key: 'Payload', value: 'payload' }, { key: 'InsideLength', value: 'insideLength' }, { key: 'Id', value: 'equipmentId' }],
            returnFunc: equipmentReturnFunc
        },
        {
            display: t('CD'),
            required: !isEditMode,
            code: 'cd',
            type: isEditMode ? 'disabled' : 'foreignKey',
            entity: 'Equipment',
            api: 'secondary',
            filterField: 'CheckDigit',
            labelFields: ['CheckDigit'],
            fields: [{ key: 'Prefix', value: 'prefix' }, { key: 'Number', value: 'number' }, { key: 'CheckDigit', value: 'cd' }, { key: 'EquipmentTypeId', value: 'equipmentTypeId' }, { key: 'EquipmentTypeCode', value: 'equipmentTypeName' }, { key: 'Payload', value: 'payload' }, { key: 'InsideLength', value: 'insideLength' }, { key: 'Id', value: 'equipmentId' }],
            returnFunc: equipmentReturnFunc
        },
        { display: t('Eq. Type ID'), code: 'equipmentTypeId', type: 'disabled' },
        { display: t('Length'), code: 'insideLength', type: 'disabled' },
        { display: t('EquipmentType'), code: 'equipmentTypeName', type: 'disabled' },
        { display: t('Payload'), code: 'payload', type: isEditMode ? 'disabled' : 'decimal' }
    ]

    const LocationFields = [
        { display: t('From'), required: true, code: 'from', type: 'foreignKey', entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'from' }, { key: 'LocationName', value: 'fromName' }, { key: 'Id', value: 'fromId' }] },
        { display: t('From') + ' ' + t('Name'), code: 'fromName', type: 'disabled' },
        { display: t('To'), required: true, code: 'to', type: 'foreignKey', entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'to' }, { key: 'LocationName', value: 'toName' }, { key: 'Id', value: 'toId' }] },
        { display: t('To') + ' ' + t('Name'), code: 'toName', type: 'disabled' },
        { display: t('Final'), code: 'final', type: 'foreignKey', entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'final' }, { key: 'LocationName', value: 'finalName' }, { key: 'Id', value: 'finalId' }] },
        { display: t('Final') + ' ' + t('Name'), code: 'finalName', type: 'disabled' },
    ]

    const MovementFields = [
        { display: t('Mov. Key'), code: 'movementKey', type: 'number' },
        {
            display: t('Movement Type'),
            required: true,
            code: 'moveType',
            type: 'foreignKey',
            entity: 'MovementType',
            api: 'secondary',
            filterField: 'Code',
            labelFields: ['Code'],
            fields: [{ key: 'Code', value: 'moveType' }, { key: 'Id', value: 'moveTypeId' }],
            returnFunc: (data, field, obj) => {
                changes('moveType', obj?.Code || '', setData)
                changes('moveTypeId', obj?.Id || '', setData)
                changes('status', '', setData)
                changes('statusId', '', setData)

                if (!obj?.Id) { setAllowedStatuses([]); return }

                movementTypeEquipmentStatusService.filter(
                    `/query?filter=MovementTypeId eq ${obj.Id}&page=1&pagesize=100`
                ).then(response => {
                    if (response?.name === 'AxiosError' || !response?.Items) {
                        setAllowedStatuses([])
                        return
                    }
                    const statuses = response.Items.map(link => ({
                        label: link.EquipmentStatusCode || '',
                        value: link.EquipmentStatusId?.toString() || '',
                    }))
                    setAllowedStatuses(statuses)
                    if (statuses.length === 1) {
                        changes('status', statuses[0].label, setData)
                        changes('statusId', statuses[0].value, setData)
                    }
                })
            }
        },
        {
            display: t('Status'),
            required: true,
            code: 'status',
            type: allowedStatuses.length === 0 ? 'disabled' : allowedStatuses.length === 1 ? 'disabled' : 'select',
            options: allowedStatuses,
            extraField: 'statusId',
            returnFunc: (data, field, obj) => {
                changes('status', obj?.label || '', setData)
                changes('statusId', obj?.value || '', setData)
            }
        },
        { display: t('Date'), required: true, code: 'date', type: 'date' },
        { display: t('Time'), required: true, code: 'time', type: 'time' },
        { display: t('EquipmentService') + ' ' + t('Code'), required: true, code: 'equipmentServiceCode', type: 'foreignKey', entity: 'EquipmentService', api: 'secondary', filterField: 'Code', labelFields: ['Code'], fields: [{ key: 'Code', value: 'equipmentServiceCode' }, { key: 'Id', value: 'equipmentServiceId' }] },
        { display: t('Condition') + ' ' + t('Code'), required: true, code: 'conditionCode', type: 'foreignKey', entity: 'EquipmentCondition', api: 'secondary', filterField: 'Code', labelFields: ['Code'], fields: [{ key: 'Code', value: 'conditionCode' }, { key: 'Id', value: 'conditionId' }] },
        { display: t('Movement') + ' ' + t('Bound'), code: 'movementBound', type: 'select', options: 'boundOptions', extraField: 'movementBoundValue' },
        { display: t('Leasing') + ' ' + t('Code'), required: true, code: 'leasingCode', type: 'foreignKey', entity: 'EquipmentLeasing', api: 'secondary', filterField: 'ContractCode', labelFields: ['ContractCode'], fields: [{ key: 'ContractCode', value: 'leasingCode' }, { key: 'Id', value: 'equipmentLeasingId' }] },
        { display: t('Transhipment'), type: 'checkBox', code: 'transhipment' },
        { display: t('Empty'), type: 'checkBox', code: 'empty' },
        {
            display: t('Shipping') + ' ' + t('Code'),
            code: 'shippingCode',
            type: 'foreignKey',
            entity: 'ShippingCondition',
            filterField: 'ShippingConditionName',
            labelFields: ['ShippingConditionCode'],
            fields: [
                { key: 'ShippingConditionCode', value: 'shippingCode' },
                { key: 'ShippingConditionName', value: 'shippingConditionName' },
                { key: 'Id', value: 'shippingCodeId' }
            ]
        },
        { display: t('Seal Number') + ' ' + t('A'), code: 'sealNumberA', type: 'text' },
        { display: t('Seal Number') + ' ' + t('B'), code: 'sealNumberB', type: 'text' },
        { type: 'separator' },
        { display: t('Movement') + ' ' + t('Comments'), code: 'comments', type: 'textArea', fullWidth: true }
    ]

    const CompaniesFields = [
        { display: t('Line'), required: true, code: 'line', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'line' }, { key: 'Id', value: 'lineId' }] },
        { display: t('Equipment') + ' ' + t('Owner Code'), required: true, code: 'equipmentOwnerCode', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'equipmentOwnerCode' }, { key: 'Id', value: 'equipmentOwnerId' }] },
        { display: t('Off-Hire'), code: 'offHire', type: 'text' },
        { display: t('Agent') + ' ' + t('Code'), code: 'agentCode', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'agentCode' }, { key: 'Id', value: 'agentId' }] },
        { display: t('Client') + ' ' + t('Code'), code: 'clientCode', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'clientCode' }, { key: 'Id', value: 'clientId' }] },
        { display: t('Shipper') + ' ' + t('Code'), code: 'shipperCode', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'shipperCode' }, { key: 'Id', value: 'shipperId' }] },
        { display: t('Consignee') + ' ' + t('Code'), code: 'consigneeCode', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'consigneeCode' }, { key: 'Id', value: 'consigneeId' }] },
    ]

    const TransportationFields = [
        { display: t('TransportResponsability'), code: 'transResp', type: 'select', options: 'transRespOptions', extraField: 'transRespValue' },
        { display: t('Transport'), required: true, code: 'transport', type: 'foreignKey', entity: 'Transport', api: 'secondary', filterField: 'TradeLine', labelFields: ['TradeLine'], fields: [{ key: 'TradeLine', value: 'transport' }, { key: 'Id', value: 'transportId' }] },
        { display: t('TransportMeansCode'), required: true, code: 'transMeansCode', type: 'foreignKey', entity: 'TransportMeans', api: 'secondary', filterField: 'Code', labelFields: ['Code'], fields: [{ key: 'Code', value: 'transMeansCode' }, { key: 'Id', value: 'transportMeansId' }] },
        { display: t('Haulier'), code: 'haulier', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'haulier' }, { key: 'Id', value: 'haulierId' }] },
        { type: 'separator' },
        { display: t('Transport') + ' ' + t('Details'), code: 'transportDetails', type: 'textArea', fullWidth: true },
    ]

    const BookingProcessFields = [
        {
            display: t('Transport') + ' ' + t('Company'),
            required: true,
            code: 'trCompany',
            type: 'foreignKey',
            entity: 'Account',
            filterField: 'AccountCode',
            labelFields: ['AccountCode'],
            fields: [{ key: 'AccountCode', value: 'trCompany' }, { key: 'Id', value: 'trCompanyId' }]
        },
        {
            display: t('Voyage'),
            required: true,
            code: 'voyage',
            type: 'foreignKey',
            entity: 'Voyage',
            filterField: 'VoyageNumber',
            labelFields: ['VoyageNumber'],
            fields: [
                { key: 'VoyageNumber', value: 'voyage' },
                { key: 'VoyageDateETA', value: 'voyageDateETA' },
                { key: 'VoyageTimeETA', value: 'voyageTimeETA' },
                { key: 'VoyageDateETD', value: 'voyageDateETD' },
                { key: 'VoyageTimeETD', value: 'voyageTimeETD' },
                { key: 'TradeBound', value: 'trBound' },
                { key: 'Id', value: 'voyageId' }
            ],
            returnFunc: (data, field, obj) => {
                field.fields?.forEach(keyValue => {
                    changes(keyValue.value, obj && obj[keyValue.key] || '', setData)
                })
                const rawBound = obj?.TradeBound || ''
                const mappedBound = tradeBoundMap[rawBound] || ''
                changes('trBound', mappedBound, setData)
                changes('trBoundValue', mappedBound, setData)
            }
        },
        { display: t('Transport') + ' ' + t('Bound'), code: 'trBound', type: 'select', options: 'boundOptions', extraField: 'trBoundValue' },
        {
            display: t('Transport') + ' ' + t('Service'),
            code: 'transportServiceCode',
            type: 'foreignKey',
            entity: 'Transport',
            api: 'secondary',
            filterField: 'TradeLine',
            labelFields: ['TradeLine'],
            fields: [{ key: 'TradeLine', value: 'transportServiceCode' }, { key: 'Id', value: 'transportServiceId' }]
        },
        {
            display: t('Booking') + ' ' + t('Number'),
            required: true,
            code: 'bookingProcess',
            type: 'foreignKey',
            entity: 'Booking',
            filterField: 'BookingNumber',
            labelFields: ['BookingNumber'],
            fields: [
                { key: 'BookingNumber', value: 'bookingProcess' },
                { key: 'Id', value: 'bookingId' },
                { key: 'ClientReference', value: 'bookingReference' },
            ],
            returnFunc: (data, field, obj) => {
                changes('bookingProcess',       obj?.BookingNumber  || '', setData)
                changes('bookingId',            obj?.Id             || '', setData)

                changes('blNumber',             '', setData)
                changes('blId',                 '', setData)
                changes('bookingReference',     '', setData)
                changes('transhipment',         false, setData)
                changes('mrn',                  '', setData)
                changes('comments',             '', setData)
                changes('documentQuantity',     '', setData)
                changes('voyage',               '', setData)
                changes('vesselCode',           '', setData)
                changes('loadingPortCode',      '', setData)
                changes('dischargePortCode',    '', setData)
                changes('originalPortCode',     '', setData)
                changes('finalPortCode',        '', setData)
                changes('clientId',             '', setData)
                changes('shipperId',            '', setData)
                changes('consigneeId',          '', setData)
                changes('notifyId',             '', setData)
                changes('externalLineId',       '', setData)
                changes('incotermsId',          '', setData)
                changes('customStatusId',       '', setData)
                changes('shippingConditionId',  '', setData)
                changes('currencyId',           '', setData)
                changes('processStatusId',      '', setData)

                if (!obj?.Id) return

                fetchBookingRelatedData(obj.Id)
            }
        },
        { display: t('BL') + ' ' + t('Number'), code: 'blNumber', type: 'disabled' },
        { display: t('Booking') + ' ' + t('Reference'), code: 'bookingReference', type: 'disabled' },
        { display: t('Goods') + ' ' + t('Details'), code: 'goodsDetails', type: 'text' },
        { display: t('UCN'), code: 'ucn', type: 'text' },
        { display: t('Nr. Packs'), code: 'nrPacks', type: 'number' },
        { display: t('Net') + ' ' + t('Weight'), code: 'netWeight', type: 'number' },
        { display: t('GrossWeight'), code: 'grossWeight', type: 'number' },
        { display: t('Temperature.'), code: 'temp', type: 'number' },
        { display: t('Temp. Unit'), code: 'tempUnit', type: 'select', options: 'tempUnitOptions', extraField: 'tempUnitValue' },
        { display: t('CommodityCode'), code: 'commodityCd', type: 'foreignKey', entity: 'Commodity', filterField: 'CommodityCode', labelFields: ['CommodityCode'], fields: [{ key: 'CommodityCode', value: 'commodityCd' }, { key: 'CommodityName', value: 'commodityName' }, { key: 'Id', value: 'commodityId' }] },
        { display: t('CommodityName'), code: 'commodityName', type: 'disabled' },
    ]

    // ─────────────────────────────────────────────────────────────────
    // Lifecycle
    // ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isFirstTime) {
            ctx.setIsLoad(true)
            getSessionStorageData(id)
            setIsFirstTime(false)
            sessionStorage.setItem(`tab${id}`, JSON.stringify(sessionKeys))

            const editingMovement = JSON.parse(sessionStorage.getItem('editingMovement'))
            const operationType = sessionStorage.getItem('operationType')

            if (editingMovement && (operationType === 'Edit' || operationType === 'Copy')) {
                editOrCopyMovement(editingMovement.Id, operationType === 'Copy')
                sessionStorage.removeItem('editingMovement')
                sessionStorage.removeItem('operationType')
            } else if (operationType === 'Create') {
                setOperation('Create')
                sessionStorage.removeItem('operationType')
            }
        }
    }, [])

    useEffect(() => {
        const newId = sessionStorage.getItem('selectedTab')
        if (newId !== id) {
            setId(newId)
            getSessionStorageData(newId)

            const editingMovement = JSON.parse(sessionStorage.getItem('editingMovement'))
            const operationType = sessionStorage.getItem('operationType')

            if (editingMovement && (operationType === 'Edit' || operationType === 'Copy')) {
                editOrCopyMovement(editingMovement.Id, operationType === 'Copy')
                sessionStorage.removeItem('editingMovement')
                sessionStorage.removeItem('operationType')
            }
        }
    })

    useEffect(() => {
        sessionKeys.forEach((key) => {
            sessionStorageMachine(id, key, key)
        })
        // eslint-disable-next-line
    }, [id, operation, data, leavingPage])

    const sessionStorageMachine = (id, key, value) => {
        if (eval(value) instanceof Object && Object.keys(eval(value)).length === 0) return
        if (eval(value) instanceof String && eval(value) === '') return
        if (eval(value) instanceof Number && eval(value) === '') return
        // eslint-disable-next-line
        if (eval(value) instanceof Map) {
            // eslint-disable-next-line
            sessionStorage.setItem(`${id}${key}`, JSON.stringify(Array.from(eval(value))))
        } else {
            // eslint-disable-next-line
            sessionStorage.setItem(`${id}${key}`, JSON.stringify(eval(value)))
        }
    }

    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true)
        const tempO  = JSON.parse(sessionStorage.getItem(`${sessionID}operation`))
        const tempD  = JSON.parse(sessionStorage.getItem(`${sessionID}data`))
        const tempOS = JSON.parse(sessionStorage.getItem(`${sessionID}open`))
        const tempPR = JSON.parse(sessionStorage.getItem(`${sessionID}passedRow`))
        const tempLP = JSON.parse(sessionStorage.getItem(`${sessionID}leavingPage`))

        if ((tempO && tempO !== '') || (tempD && Object.keys(tempD).length !== 0) || tempOS || (tempPR && Object.keys(tempD).length !== 0)) {
            setOperation(new String(tempO))
            setData(new Object(tempD))
            setOpen(tempOS)
            setPassedRow(tempPR)
            setLeavingPage(tempLP || false)
            ctx.setIsLoad(false)
            return
        }
        get()
    }

    const get = () => {
        ctx.setIsLoad(false)
    }

    const refreshMovement = () => {
        ctx.setIsLoad(true)
        sessionKeys.forEach(key => { sessionStorage.removeItem(`${id}${key}`) })
        setData({})
        setPassedRow({})
        setOpen(false)
        setOperation('')
        setLeavingPage(false)
        setIsEditMode(false)
        setEditingId(null)
        get()
        ctx.setIsLoad(false)
        ctx.toastMessages('success', t('Form refreshed'), '')
    }

    // ─────────────────────────────────────────────────────────────────
    // Error handling
    // ─────────────────────────────────────────────────────────────────
    const handleError = (error) => {
        let errorMessage = t('Error saving') + t('Movement')
        const data = error.response?.data

        if (Array.isArray(data)) {
            const message = data.map(e => `${e.field}: ${e.message}`).join('\n')
            setApiErrorMessage(message)
            setApiErrorOpen(true)
            return
        } else if (data?.errors) {
            errorMessage = Object.entries(data.errors).map(([field, messages]) => `${field}: ${messages.join(', ')}`).join('; ')
        } else if (data?.message) {
            errorMessage = data.field ? `${data.field}: ${data.message}` : data.message
        } else if (data?.detail) {
            errorMessage = data.detail
        } else if (data?.title) {
            errorMessage = data.title
        } else if (error.message) {
            errorMessage = error.message
        }
        ctx.toastMessages('error', '', errorMessage)
    }

    // ─────────────────────────────────────────────────────────────────
    // Date helpers
    // ─────────────────────────────────────────────────────────────────
    const formatDateToISO = (dateValue) => {
        if (!dateValue) return null
        try {
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}T/)) return dateValue
            let date
            if (typeof dateValue === 'string' && dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                const [month, day, year] = dateValue.split('-')
                date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)))
            } else if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = dateValue.split('-')
                date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)))
            } else {
                date = new Date(dateValue)
            }
            if (isNaN(date.getTime())) return null
            return date.toISOString()
        } catch (error) {
            console.error('Error formatting date to ISO:', error)
            return null
        }
    }

    const combineDateAndTime = (dateValue, timeValue) => {
        if (!dateValue) return null
        try {
            let date
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = dateValue.split('-')
                date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)))
            } else {
                date = new Date(dateValue)
            }
            if (isNaN(date.getTime())) return null
            if (timeValue) {
                const [hours, minutes] = timeValue.split(':')
                date.setUTCHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0)
            }
            return date.toISOString()
        } catch (error) {
            console.error('Error combining date and time:', error)
            return null
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Save
    // ─────────────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!data.moveType || !data.status || !data.date || !data.time || !data.equipmentServiceCode || !data.conditionCode || !data.leasingCode || !data.from || !data.to || !data.line || !data.trCompany || !data.transportId || !data.voyage) {
            ctx.toastMessages('error', '', t('Missing required fields'))
            return
        }

        ctx.setIsLoad(true)

        // ── WebAPI Data (BookingEquipment) ─────────────────────────────
        const webApiData = {
            // Campo movement
            ShippingConditionId: data.shippingCodeId || null,
            Transhipment: data.transhipment || false,
            SealNumberA: data.sealNumberA || '',
            SealNumberB: data.sealNumberB || '',
            GoodsDetails: data.goodsDetails || '',
            // COMPANIES
            AgentId: data.agentId || null,
            OffHire: data.offHire || '',
            // Booking process
            TransportServiceId: data.transportServiceId ? parseInt(data.transportServiceId) : null,
            TransportServiceCode: data.transportServiceCode || '',
            UCN: data.ucn || '',
            Temp: data.temp ? parseFloat(data.temp) : null,
            TempUnit: data.tempUnit || '',
            // Transportation
            TransportDetails: data.transportDetails || '',
            // Customs
            CustomsExportDeclaration: data.goodsExportDeclaration || '',
            CustomsDocumentNumber: data.customsDispatchDocument || '',
            GoodValue: data.goodValue ? parseFloat(data.goodValue) : null,
            Currency: data.currency || '',
            CustomsSeal: data.customsSeal || '',
            CreateCargo: data.createCargo || false,
        }

        // ── ContainerControl Data (Movement) ───────────────────────────
        const movementData = {
            // ── Equipment ────────────────────────────────────────────
            EquipmentId:            [parseInt(data.equipmentId)],                       // List<int>

            // ── Movement core ────────────────────────────────────────
            Date:                   combineDateAndTime(data.date, data.time),            // DateTime
            Status:                 data.status                         || '',           // string
            MovementKey:            parseInt(data.movementKey)               || null,         // int?
            MovementTypeId:         parseInt(data.moveTypeId)           || 0,            // int
            EquipmentStatusId:      parseInt(data.statusId)             || 0,            // int
            EquipmentServiceId:     parseInt(data.equipmentServiceId)   || 0,            // int
            EquipmentConditionId:   parseInt(data.conditionId)          || 0,            // int
            EquipmentLeasingId:     parseInt(data.equipmentLeasingId)   || 0,            // int
            TransportMeansId:       parseInt(data.transportMeansId)       || 0,            // int  (transMeansCode stores the Id)
            TransportId:            parseInt(data.transportId)          || 0,            // int
            MovementBound:          data.movementBound || data.movementBoundValue || '', // string?
            Comments:               data.comments                       || '',           // string
            TransportResponsibility: data.transResp || data.transRespValue || '',        // string
            MovementOfHire:         data.offHire                        || '',           // string
            ShipmentUCN:            data.ucn                            || '',           // string
            Days:                   0,                                                   // int (default)
            IsLast:                 false,                                               // bool (default)
            Empty:                  data.empty                         || false,        // bool

            // ── Location ─────────────────────────────────────────────
            MovementFromId:         data.fromId                         || '00000000-0000-0000-0000-000000000000',   // Guid
            MovementFromCode:       data.from                           || '',           // string
            MovementFromName:       data.fromName                       || null,         // string?
            MovementToId:           data.toId                           || null,         // Guid?
            MovementToCode:         data.to                             || null,         // string?
            MovementToName:         data.toName                         || null,         // string?
            MovementFinalId:        data.finalId                        || '00000000-0000-0000-0000-000000000000',   // Guid
            MovementFinalCode:      data.final                          || '',           // string
            MovementFinalName:      data.finalName                      || null,         // string?
            RestitutionId:          data.restitutionCodeId              || '00000000-0000-0000-0000-000000000000',   // Guid
            RestitutionCode:        data.restitutionCode                || null,         // string?
            RestitutionName:        data.restitutionName                || null,         // string?

            // ── Voyage ───────────────────────────────────────────────
            MovementVoyageId:       data.voyageId                       || '00000000-0000-0000-0000-000000000000',   // Guid
            VoyageNumber:           data.voyage                         || null,         // string?
            VoyageDateETA:          formatDateToISO(data.voyageDateETA) || '0001-01-01T00:00:00.000Z',              // DateTime
            VoyageTimeETA:          parseInt(data.voyageTimeETA)        || null,         // int?
            VoyageDateETD:          formatDateToISO(data.voyageDateETD) || '0001-01-01T00:00:00.000Z',              // DateTime
            VoyageTimeETD:          parseInt(data.voyageTimeETD)        || null,         // int?

            // ── Company IDs (Guid) ────────────────────────────────────
            BookingTransportId:     data.trCompanyId                    || '00000000-0000-0000-0000-000000000000',   // Guid
            TransferCompanyId:      data.trCompanyId                    || '00000000-0000-0000-0000-000000000000',   // Guid
            TransferCompanyName:    data.trCompany                      || null,         // string?
            AccountLine:            data.line                           || null,         // string?
            LineId:                 data.lineId                         || '00000000-0000-0000-0000-000000000000',   // Guid
            EquipmentOwnerId:       data.equipmentOwnerId               || '00000000-0000-0000-0000-000000000000',   // Guid
            ClientId:               data.clientId                       || null,         // Guid?
            ShipperId:              data.shipperId                      || null,         // Guid?
            ConsigneeId:            data.consigneeId                    || null,         // Guid?
            HaulierId:              data.haulierId                      || null,         // Guid?
            AgentId:                data.agentId                         || null,         // Guid?
            AgentCode:              data.agentCode                       || '',           // string?

            // ── Booking link ─────────────────────────────────────────
            BookingId:              data.bookingId                      || null,         // Guid?
            BookingEquipmentId:     data.bookingEquipmentId             || '00000000-0000-0000-0000-000000000000',   // Guid
            BookingEquipmentCode:   data.bookingReference               || null,         // string?
            BookingEquipmentName:   null,                                                // string?
            BookingTransportName:   data.trCompany                      || null,         // string?
            CustomsSeal:            data.customsSeal                     || '',           // string?

            // ── Required by BaseRequest (BusinessUnit/Access) ─────────
            BusinessUnitId:         '00000000-0000-0000-0000-000000000000',              // Guid — update if available
            AccessUserId:           '00000000-0000-0000-0000-000000000000',              // Guid — update if available
        }

        try {
            if (isEditMode && data.bookingEquipmentId && data.bookingEquipmentId !== '00000000-0000-0000-0000-000000000000') {
                const webApiResponse = await bookingEquipmentService.update(data.bookingEquipmentId, webApiData)
                if (webApiResponse.name === 'AxiosError') {
                    ctx.setIsLoad(false)
                    handleError(webApiResponse)
                    return
                }
            }

            if (isEditMode && data.bookingId) {
                // Usar shipmentId guardado no load; se não existir, fazer filter por BookingId
                let shipmentId = data.shipmentId || null
                if (!shipmentId) {
                    const shipmentSearch = await shipmentService.filter(`/query?filter=IdBooking eq ${data.bookingId}&page=1&pagesize=1`)
                    shipmentId = shipmentSearch?.Items?.[0]?.Id || null
                }
                if (shipmentId) {
                    const shipmentData = {
                        UCN: data.ucn || '',
                        Temp: data.temp ? parseFloat(data.temp) : null,
                        NrPacks: data.nrPacks ? parseInt(data.nrPacks) : null,
                        NetWeight: data.netWeight ? parseFloat(data.netWeight) : null,
                        MarineWeight: data.grossWeight ? parseFloat(data.grossWeight) : null,
                        IdCommodity: data.commodityId || null,
                    }
                    const shipmentResponse = await shipmentService.update(shipmentId, shipmentData)
                    if (shipmentResponse.name === 'AxiosError') {
                        ctx.setIsLoad(false)
                        handleError(shipmentResponse)
                        return
                    }
                }
            }

            const savePromise = isEditMode && editingId
                ? movementService.update(editingId, movementData)
                : movementService.post(movementData)

            const response = await savePromise

            if (response.name === 'AxiosError') {
                ctx.setIsLoad(false)
                handleError(response)
                return
            }

            const successMessage = isEditMode ? t('Movement') + t('updatedSucessfully') : t('Movement') + t('savedSuccessfully')
            ctx.setIsLoad(false)
            ctx.toastMessages('success', '', successMessage)
            sessionKeys.forEach(key => sessionStorage.removeItem(`${id}${key}`))
            sessionStorage.removeItem('editingMovement')
            sessionStorage.removeItem('operationType')
            ctx2.closeTab(JSON.parse(id), 'lastMovementList')
        } catch (error) {
            ctx.setIsLoad(false)
            handleError(error)
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Edit / Copy
    // ─────────────────────────────────────────────────────────────────
    const editOrCopyMovement = (movementId, isCopy = false) => {
        ctx.setIsLoad(true)

        lastMovementService.getById(movementId).then(response => {
            console.log('Movement data loaded for edit/copy:', response.id)
            console.log('Response data:', movementId, response)

            ctx.setIsLoad(false)

            if (response.name === 'AxiosError') { ctx.toastMessages('error', '', t('Failed to load') + t('Movement')); return }
            if (!response) { ctx.toastMessages('error', '', t('Movement') + ' ' + t('not found')); return }

            const movementData = {
                equipmentTypeId:     response.EquipmentTypeId?.toString(),
                equipmentId:         response.EquipmentId?.toString(),
                id:                  response.Id?.toString(),
                prefix:              response.EquipmentPrefix         || '',
                number:              response.EquipmentNumber?.toString() || '',
                cd:                  response.EquipmentCheckDigit?.toString() || '',
                insideLength:        response.EquipmentInsideLength?.toString() || '',
                payload:             response.EquipmentPayload?.toString() || '',
                equipmentTypeName:   response.TypeName                || '',
                moveTypeId:          response.MovementTypeId?.toString(),
                movementKey:         response.MovementKey?.toString()     || '',
                moveType:            response.MovementTypeCode        || '',
                statusId:            response.EquipmentStatusId?.toString(),
                status:              response.EquipmentStatusCode     || '',
                date:                response.Date?.split('T')[0]     || '',
                time:                response.Date?.split('T')[1]?.substring(0, 5) || '',
                equipmentServiceId:  response.EquipmentServiceId?.toString(),
                equipmentServiceCode: response.EquipmentServiceCode  || '',
                conditionId:         response.EquipmentConditionId?.toString(),
                conditionCode:       response.EquipmentConditionCode  || '',
                movementBound:       response.MovementBound           || '',
                movementBoundValue:  response.MovementBound           || '',
                equipmentLeasingId:  response.EquipmentLeasingId?.toString(),
                leasingCode:         response.LeasingContractCode     || '',
                transhipment:        response.Transhipment === true || response.Transhipment === 'true',
                empty:               response.IsEmpty                 || false,
                sealNumberA:         response.SealNumberA             || '',
                sealNumberB:         response.SealNumberB             || '',
                shippingCode:        response.ShippingConditionCode   || '',
                shippingCodeId:      response.ShippingConditionId?.toString() || null,
                comments:            response.Comments                || '',
                fromId:              response.MovementFromId?.toString(),
                from:                response.MovementFromCode        || '',
                fromName:            response.MovementFromName        || '',
                toId:                response.MovementToId?.toString(),
                to:                  response.MovementToCode          || '',
                toName:              response.MovementToName          || '',
                finalId:             response.MovementFinalId?.toString(),
                final:               response.MovementFinalCode       || '',
                finalName:           response.MovementFinalName       || '',
                restitutionId:       response.RestitutionId?.toString(),
                restitutionCode:     response.RestitutionCode         || '',
                restitutionName:     response.RestitutionName         || '',
                lineId:              response.LineId?.toString(),
                line:                response.AccountLine             || '',
                equipmentOwnerId:    response.EquipmentOwnerId?.toString(),
                equipmentOwnerCode:  response.EquipmentOwnerCode?.toString() || '',
                equipmentOwnerName:  response.EquipmentOwnerName      || '',
                offHire:             response.MovementOfHire          || '',
                agentId:             response.AgentId?.toString()     || null,
                agentCode:           response.AgentCode               || '',
                agentName:           response.AgentName               || '',
                clientId:            response.ClientId?.toString()    || null,
                clientCode:          response.ClientCode              || '',
                shipperId:           response.ShipperId?.toString()   || null,
                shipperCode:         response.ShipperCode             || '',
                consigneeId:         response.ConsigneeId?.toString() || null,
                consigneeCode:       response.ConsigneeCode           || '',
                haulierId:           response.HaulierId?.toString()   || null,
                haulier:             response.HaulierCode             || '',
                haulierCode:         response.HaulierCode             || '',
                haulierName:         response.HaulierName             || '',
                transportDetails:    response.TransportDetails        || '',
                transResp:           response.TransportResponsibility || '',
                transRespValue:      response.TransportResponsibility || '',
                transportId:         response.TransportId?.toString(),
                transport:           response.TransportName           || '',
                transportMeansId:    response.TransportMeansId?.toString(),
                transMeansCode:      response.TransportMeansCode?.toString() || '',
                trCompanyId:         response.TransferCompanyId?.toString(),
                trCompany:           response.TransferCompanyName     || '',
                voyageId:            response.MovementVoyageId?.toString(),
                voyage:              response.VoyageNumber            || '',
                voyageDateETA:       response.VoyageDateETA ? response.VoyageDateETA.split('T')[0] : '',
                voyageTimeETA:       response.VoyageTimeETA           || '',
                voyageDateETD:       response.VoyageDateETD ? response.VoyageDateETD.split('T')[0] : '',
                voyageTimeETD:       response.VoyageTimeETD           || '',
                trBound:             tradeBoundMap[response.TrBound]  || response.TrBound || '',
                trBoundValue:        tradeBoundMap[response.TrBound]  || response.TrBound || '',
                transportServiceId:  response.TransportServiceId?.toString() || null,
                transportServiceCode: response.TransportServiceCode || '',
                bookingEquipmentId:  response.BookingEquipmentId      || null,
                bookingProcess:      response.BookingNumber           || '',
                bookingReference:    response.ClientReference         || '',
                blNumber:            response.BlNumber                || '',
                goodsDetails:        response.GoodsDetails            || '',
                ucn:                 response.ShipmentUCN             || '',
                nrPacks: response.Package?.toString() || '',
                netWeight: response.NetWeight?.toString() || '',
                grossWeight:         response.MarineWeight?.toString() || '',
                temp: response.Temp?.toString() || '',
                tempUnit: response.TempUnit || '',
                tempUnitValue: response.TempUnit || '',
                commodityId:         response.CommodityId?.toString() || null,
                commodityCd: response.CommodityCode || '',
                commodityName: response.CommodityName || '',
                bookingId:           response.BookingId?.toString()   || null,
                shipmentId:          response.ShipmentId?.toString()  || null,
                goodsExportDeclaration:  response.CustomsExportDeclaration || '',
                customsDispatchDocument: response.CustomsDocumentNumber    || '',
                goodValue:               response.GoodValue?.toString()    || '',
                currency:                response.Currency                 || '',
                customsSeal:             response.CustomsSeal              || '',
                createCargo:             response.CreateCargo              || false,
                _isCopy:                 isCopy,
                _originalId:             isCopy ? movementId : null
            }

            if (isCopy) {
                delete movementData.id
                delete movementData.ExternalId
                delete movementData.CreatedAt
                delete movementData.UpdatedAt
                setIsEditMode(false)
                setEditingId(null)
                setOperation('Create')
                if (movementData.equipmentId) autoIncrementMovKey(movementData.equipmentId, true)
            } else {
                setIsEditMode(true)
                setEditingId(movementId)
                setOperation('Edit')
            }

            setData(movementData)

            if (response.MovementTypeId) {
                movementTypeEquipmentStatusService.filter(
                    `/query?filter=MovementTypeId eq ${response.MovementTypeId}&page=1&pagesize=100`
                ).then(res => {
                    if (res?.Items) {
                        setAllowedStatuses(res.Items.map(link => ({
                            label: link.EquipmentStatusCode || '',
                            value: link.EquipmentStatusId?.toString() || '',
                        })))
                    }
                })
            }

            if (isCopy && response.BookingId) {
                fetchBookingRelatedData(response.BookingId)
            }

        }).catch(error => {
            ctx.setIsLoad(false)
            console.error('Error loading movement:', error)
            ctx.toastMessages('error', '', t('Failed to load') + ' ' + t('Movement'))
        })
    }

    // ─────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────
    return (
        <div>
            <M_Header
                element={
                    <>
                        <M_Button
                            tooltip={t("Back")}
                            label={t("Back")}
                            icon={<i className="pi pi-arrow-left iconTable" style={{ width: '20px', color: ctx.secondaryColor ? ctx.secondaryColor : 'var(--color-black)' }}></i>}
                            onClick={() => { setLeavingPage(true) }}
                        />
                        <M_Button tooltip={t("Refresh")} icon={<Refresh />} label={t("Refresh")} onClick={() => refreshMovement()} />
                        <M_Button tooltip={t("Save")} icon={<i className="pi pi-save"></i>} label={t("Save")} onClick={() => onSave()} />
                    </>
                }
            />
            <div style={{ margin: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: !matches10 && 'auto' || 'auto auto' }}>
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><i className="pi pi-box" style={{ marginRight: '10px', scale: '1.3' }} /> {t("Equipment")}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches15 && 'auto' || !matches13 && 'auto auto' || 'auto auto auto', justifyContent: 'space-evenly' }}>
                                {EquipmentFields.map((field, index) => fields(field, index, data, setData, null, field.returnFunc || null))}
                            </div>
                        }
                    />
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><Anchor style={{ marginRight: '10px', scale: '1.3' }} /> {t("Location")}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches15 && 'auto' || !matches13 && 'auto auto' || 'auto auto', justifyContent: 'space-evenly' }}>
                                {LocationFields.map((field, index) => {
                                    if (field.type === 'separator') return <div key={`separator-${index}`} style={{ gridColumn: '1 / -1', height: '0' }} />
                                    if (field.fullWidth) return (<div key={index} style={{ gridColumn: '1 / -1', width: '100%', marginTop: '10px' }}>{fields(field, index, data, setData, false, null, '100%')}</div>)
                                    return fields(field, index, data, setData, null, null)
                                })}
                            </div>
                        }
                    />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: !matches10 && 'auto' || 'auto auto' }}>
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><Cached style={{ marginRight: '10px', scale: '1.3' }} /> {t("Movement")}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches15 && 'auto' || !matches13 && 'auto auto' || 'auto auto auto', justifyContent: 'space-evenly' }}>
                                {MovementFields.map((field, index) => {
                                    if (field.type === 'separator') return <div key={`separator-${index}`} style={{ gridColumn: '1 / -1', height: '0' }} />
                                    if (field.fullWidth) return (<div key={index} style={{ gridColumn: '1 / -1', width: '100%', marginTop: '10px' }}>{fields(field, index, data, setData, false, null, '100%')}</div>)
                                    const fieldOptions = field.code === 'status' ? allowedStatuses : field.code === 'movementBound' ? boundOptions : null
                                    return fields(field, index, data, setData, null, field.returnFunc || null, '250px', fieldOptions)
                                })}
                            </div>
                        }
                    />
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><Group style={{ marginRight: '10px', scale: '1.3' }} /> {t("Companies")}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches15 && 'auto' || !matches13 && 'auto auto' || 'auto auto', justifyContent: 'space-evenly' }}>
                                {CompaniesFields.map((field, index) => fields(field, index, data, setData))}
                            </div>
                        }
                    />
                </div>
                <div>
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><Book style={{ marginRight: '10px', scale: '1.3' }} /> {t('Booking') + ' ' + t('Process')}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches15 && 'auto' || !matches13 && 'auto auto' || !matches9 && 'auto auto auto' || !matches5 && 'auto auto auto auto' || 'auto auto auto auto auto', justifyContent: 'space-evenly' }}>
                                {BookingProcessFields.map((field, index) => fields(field, index, data, setData, null, field.returnFunc || null, null, fieldOptionsMap[field.code]))}
                            </div>
                        }
                    />
                </div>
                <div>
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><DirectionsBoat style={{ marginRight: '10px', scale: '1.3' }} /> {t("Transportation")}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches15 && 'auto' || !matches13 && 'auto auto' || !matches7 && 'auto auto auto' || 'auto auto auto auto', justifyContent: 'space-evenly', gap: '0 8px' }}>
                                {TransportationFields.map((field, index) => {
                                    if (field.type === 'separator') return <div key={`separator-${index}`} style={{ gridColumn: '1 / -1', height: '0' }} />
                                    if (field.fullWidth) return (<div key={index} style={{ gridColumn: '1 / -1', width: '100%', marginTop: '10px' }}>{fields(field, index, data, setData, false, null, '100%')}</div>)
                                    return fields(field, index, data, setData, null, null, '200px', transRespOptions)
                                })}
                            </div>
                        }
                    />
                </div>
                <div style={{ margin: '25px' }}></div>
            </div>
            <M_MessageBoard
                warning
                open={leavingPage}
                title={t("leavingPageTitle")}
                body={t("leavingPageBody")}
                footer={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-red)'} onClick={() => setLeavingPage(false)} label={t("ButtonCancel")} />
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-green)'} onClick={() => ctx2.closeTab(JSON.parse(id))} label={t("ButtonConfirm")} />
                    </span>
                }
            />
            <M_MessageBoard
                open={apiErrorOpen}
                title={t("Validation Error")}
                body={<pre style={{ margin: 0 }}>{apiErrorMessage}</pre>}
                warning
                footer={
                    <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-red)'} onClick={() => setApiErrorOpen(false)} label={t("OK")} />
                }
            />
        </div>
    )
}